import { Component, computed, inject, signal } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { StyleClassModule } from 'primeng/styleclass';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { Avatar } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { LayoutService } from '@/app/layout/service/layout.service';
import { AppProfile } from './app.profile';
import { ThemeService } from '@/app/core/services/theme/theme.service';
import { UserStateService } from '@/app/features/user/services';

@Component({
  selector: '[app-header]',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    StyleClassModule,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    Avatar,
    AppProfile,
  ],
  template: `
    <div class="topbar-left">
      @if (showMenuButton()) {
        <button
          id="sidebar-toggle-btn"
          type="button"
          (click)="layoutService.toggleMenu()"
          class="menu-button"
          aria-label="Abrir menú de navegación"
        >
          <i class="pi pi-bars"></i>
        </button>
        <span class="topbar-separator"></span>
      }
      <div class="page-title">
        {{ pageTitle() }}
      </div>
    </div>

    <div class="topbar-right">
      <div class="topbar-actions">
        <button
          id="mobile-sidebar-toggle-btn"
          type="button"
          (click)="layoutService.toggleMenu()"
          class="menu-button menu-button-mobile"
          aria-label="Abrir menú de navegación"
        >
          <i class="pi pi-bars"></i>
        </button>

        <button
          type="button"
          (click)="layoutService.toggleConfigSidebar()"
          class="app-config-button"
        >
          <i class="pi pi-cog"></i>
        </button>

        <div class="relative">
          <a
            id="header-avatar-btn"
            pStyleClass="@next"
            enterFromClass="hidden"
            enterActiveClass="p-anchored-overlay-enter-active"
            leaveActiveClass="p-anchored-overlay-leave-active"
            leaveToClass="hidden"
            [hideOnOutsideClick]="true"
            class="flex items-center cursor-pointer"
            [pTooltip]="userName()"
            tooltipPosition="bottom"
          >
            <p-avatar
              [label]="userInitial()"
              styleClass="!rounded-md !overflow-hidden !w-9 !h-9"
              [style]="{ 'background-color': '#ece9fc', color: '#2a1261' }"
            />
          </a>
          <div class="absolute hidden top-full right-0 mt-2 z-20">
            <div app-profile class="w-52"></div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class AppHeader {
  layoutService = inject(LayoutService);
  themeService = inject(ThemeService);
  private readonly userStateService = inject(UserStateService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  search = signal('');

  showMenuButton = computed(() => {
    const menuMode = this.layoutService.layoutConfig().menuMode;
    return menuMode === 'overlay' || menuMode === 'static';
  });

  userName = computed(() => {
    const data = this.userStateService.userData();
    return data?.firstName || 'Usuario';
  });

  userInitial = computed(() => this.userName().charAt(0).toUpperCase());

  private pageTitle$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map(() => {
      let route = this.activatedRoute;
      while (route.firstChild) {
        route = route.firstChild;
      }
      return route.snapshot.data['title'] || 'Dashboard';
    }),
  );

  pageTitle = toSignal(this.pageTitle$, { initialValue: 'Dashboard' });
}
